import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'components/Context/AppContext';
import { AreaCharter, Button, InputField } from 'components/Global';
import ImageViewer from 'components/Global/ImageViewer';
import AddOnsField from 'components/Global/AddOnsField';
import PropTypes from 'prop-types';

import { ReactComponent as MappinIcon } from 'icons/mappin-icon.svg';
import { ReactComponent as ReuploadIcon } from 'icons/reupload-icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeOrderStatus,
  changePaymentType,
  fetchOrderByIdWithApproval,
  getOrderAddOnsById,
  updateOrderCancelation,
} from 'features/orders/actions';
import { getPaymentMethods } from 'features/payment/actions';
import { getReviewByOrder } from 'features/reviews/actions';

import { ReactComponent as Duration } from 'icons/duration-icon.svg';
import { ReactComponent as Calendar } from 'icons/calendar-icon.svg';
import { ReactComponent as PointsIcon } from 'icons/points-icon.svg';

import VehicleDetailComponent from './VehicleDetailComponent';
import RentTimeDetailComponents from './RentTimeDetailComponents';
import { getAllRentalLocation } from 'features/rental-location/actions';
import Pembayaran from './PembayaranComponent';
import DriverData from './DriverData';
import {
  checkEmptyObject,
  getKTPValue,
  getPassportValue,
  getPaymentValue,
  mappedAreaCharter,
  setCurrency,
} from 'utils/functionality';
import clsx from 'clsx';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { updateApprovalStatus } from 'features/approvals/actions';
import { trackEvent } from 'utils/analytics';

// export const PERMISSION_ALLOWED_ROLE = ['Admin Supervisor', 'Administration', 'Administration Manager'];

const RenderedProcessButton = ({ data, paymentMethod, customer, isPaymentValid, handleProcess }) => {
  const isOrderApproval = data?.approval !== null;
  const isManualTransfer =
    paymentMethod?.find(
      (item) =>
        item.id ===
        (isOrderApproval
          ? data?.approval?.data?.disbursement?.payment_method_id
          : data?.disbursement?.payment_method_id),
    )?.method === 'Manual Transfer';
  const showButtonForConfirmation =
    isManualTransfer &&
    data.order_status === 'CHECKOUT' &&
    data?.order_approval_status !== 'WAITING_APPROVAL_DELETE_ORDER';
  const showButtonForUnprocess =
    (data.order_status === 'PAID' || data.order_status === 'REVIEWING_IDENTITY') &&
    !customer?.PersonalInfos?.need_review_sim &&
    !customer?.PersonalInfos?.need_review_ktp &&
    !data.order_approval_status;

  if (showButtonForConfirmation) {
    return (
      <Button
        variant="success"
        className={clsx('button', !isPaymentValid && 'button--disabled')}
        width={208}
        size="sm"
        disabled={!isPaymentValid}
        onClick={handleProcess}
      >
        Proses
      </Button>
    );
  } else if (showButtonForUnprocess) {
    return (
      <Button variant="success" className="button" width={208} size="sm" onClick={handleProcess}>
        Proses
      </Button>
    );
  } else {
    return null;
  }
};

const RegularDataForm = ({
  data,
  customer,
  selectedCountry,
  imagePreviewHandler,
  approvalUpdate,
  getFileName,
  showCancelOrderBtn,
  showRejectOrderBtn,
  transactionKey,
  isPaymentValid,
  setShowBankTransferConfirmation,
  driverData,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { setShowReviewIdentityModal, setShowConfirmation, setShowRejectOrderModal, setShowSpinner, showToast } =
    useAppContext();

  const { data: allLocationData } = useSelector((state) => state.rentalLocation);
  const paymentMethod = useSelector((state) => state.payment.data);

  const user = useSelector((state) => state.user.profile);
  const isPublishTaskCharterFlowEnabled = useFliptBoolean(
    'FLAG_FE_PUBLISH_TASK_CHARTER_FLOW',
    false,
    user.id || 'anonymous',
  );
  const isCharterIdentityReviewEnabled = useFliptBoolean('FLAG_FE_REVIEW_IDENTITY_CHARTER', false, 'anonymous');
  const isUploadNewDocumentEnabled = useFliptBoolean('FLAG_FE_NEW_UPLOAD_DOCUMENT', false, 'anonymous');

  useEffect(() => {
    dispatch(fetchOrderByIdWithApproval(transactionKey));
    dispatch(getOrderAddOnsById(transactionKey));
    dispatch(getPaymentMethods());
    dispatch(getAllRentalLocation());
    dispatch(getReviewByOrder(transactionKey));
  }, [transactionKey]);

  const backHandler = () => {
    navigate(-1);
  };

  const handleRequestCancelOrder = () => {
    const payload = {
      transaction_key: data.transaction_key,
      status: 'REFUNDED',
    };

    setShowConfirmation({
      message: 'Apakah anda yakin ingin membatalkan pesanan ini?',
      show: true,
      onClick: async () => {
        try {
          setShowSpinner(true);
          trackEvent('order_cancellation_confirmed', {
            order_id: data?.transaction_key,
            source: 'detail_order_car_charter_regular',
          });
          await dispatch(updateOrderCancelation(payload)).unwrap();
          showToast({ type: 'success', message: 'Berhasil Membatalkan Pesanan' });
          navigate(-1);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Cancel order error:', err);
          showToast({ type: 'error', message: 'Gagal Membatalkan Pesanan!' });
        } finally {
          setShowSpinner(false);
        }
      },
    });
  };

  const handleProcess = () => {
    let prevStatus;
    let nextStatus;
    let confirmationMessage;
    let toastSuccessMessage;
    let toastErrorMessage;

    switch (data?.order_status) {
      case 'CHECKOUT':
        prevStatus = 'CHECKOUT';
        nextStatus = 'PAID';
        confirmationMessage = 'Apakah anda yakin ingin mengkonfirmasi transaksi?';
        toastSuccessMessage = 'Transaksi Berhasil Dikonfirmasi';
        toastErrorMessage = 'Transaksi Gagal Dikonfirmasi!';
        break;
      case 'PAID':
        prevStatus = 'PAID';
        nextStatus = 'COMPLETED';
        confirmationMessage =
          data.type === 'HALF'
            ? `Orderan ini menggunakan pembayaran DP ${
                data?.order_detail?.dp_rule?.formula_percentage?.value
                  ? `${data.order_detail.dp_rule.formula_percentage?.value}%`
                  : ''
              }. Sebelum melanjutkan transaksi, pastikan pelanggan telah membayar sisa DP terlebih dahulu. Tetap lanjutkan?`
            : 'Apakah anda yakin ingin memproses transaksi?';
        toastSuccessMessage = 'Transaksi Berhasil Diproses';
        toastErrorMessage = 'Transaksi Gagal Diproses!';
        break;
      case 'REVIEWING_IDENTITY':
        prevStatus = 'REVIEWING_IDENTITY';
        nextStatus = 'COMPLETED';
        confirmationMessage =
          data.type === 'HALF'
            ? `Orderan ini menggunakan pembayaran DP ${
                data?.order_detail?.dp_rule?.formula_percentage?.value
                  ? `${data.order_detail.dp_rule.formula_percentage?.value}%`
                  : ''
              }. Sebelum melanjutkan transaksi, pastikan pelanggan telah membayar sisa DP terlebih dahulu. Tetap lanjutkan?`
            : 'Apakah anda yakin ingin memproses transaksi?';
        toastSuccessMessage = 'Transaksi Berhasil Diproses';
        toastErrorMessage = 'Transaksi Gagal Diproses!';
        break;
      default:
        showToast({ type: 'error', message: 'Status order tidak valid!' });
        return;
    }

    const payload = {
      prevStatus,
      nextStatus,
      transaction_key: data?.transaction_key,
    };

    setShowConfirmation({
      message: confirmationMessage,
      show: true,
      onClick: async () => {
        try {
          trackEvent('order_status_advanced', {
            order_id: data?.transaction_key,
            to_status: nextStatus,
            source: 'detail_order_car_charter_regular',
            via_approval: Boolean(data?.order_approval_status?.includes('UPDATE_ORDER')),
          });
          if (data?.order_approval_status?.includes('UPDATE_ORDER')) {
            await dispatch(updateApprovalStatus({ status: 'approved', id: data?.approval?.id, skip: true })).unwrap();
          } else {
            await dispatch(changeOrderStatus(payload)).unwrap();
          }
          showToast({ type: 'success', message: toastSuccessMessage });

          if (data.type === 'HALF' && data.order_status === 'PAID') {
            await dispatch(changePaymentType(data?.transaction_key)).unwrap();
          }

          navigate(-1);
        } catch (err) {
          showToast({ type: 'error', message: toastErrorMessage });
        }
      },
    });
  };

  return (
    <div className="detail-car-charter__form">
      {driverData && !checkEmptyObject(driverData) && (
        <div className="detail-driver">
          <h2>Data Driver</h2>
          <DriverData driverData={driverData} />
        </div>
      )}

      <div className="detail-penyewa">
        <h2>Detail Penyewa</h2>
        <div className="detail-penyewa__data">
          <InputField
            label="Lokasi"
            disable
            readOnly
            icon={<MappinIcon />}
            iconPosition={'start'}
            value={allLocationData?.find((loc) => loc.id === data?.order_detail?.location_id)?.name || '-'}
          />

          <InputField label="Name" disable readOnly value={data?.user_name || '-'} />

          <div className="detail-penyewa__data__phone-number">
            <InputField
              label="No Handphone"
              readOnly
              disable
              value={data?.is_admin_creation ? data?.phone_country_code : customer?.phone_code || '-'}
              icon={
                <img
                  src={`https://flagcdn.com/w2560/${selectedCountry?.code?.toLowerCase()}.png`}
                  width={26}
                  height={16}
                  alt={selectedCountry?.name + ' nation flag'}
                />
              }
              iconPosition={'end'}
            />
            <InputField value={data?.phone_number || '-'} disable readOnly />
          </div>

          <InputField label="Email" disable readOnly value={data?.email || '-'} />

          <ImageViewer
            label="KTP"
            value={getKTPValue(data, customer, isUploadNewDocumentEnabled)}
            onClick={imagePreviewHandler}
            customButton={
              isCharterIdentityReviewEnabled &&
              (data?.order_status === 'PAID' || data?.order_status === 'REVIEWING_IDENTITY') &&
              !approvalUpdate && (
                <>
                  <Button type="button" onClick={() => setShowReviewIdentityModal({ type: 'ktp', show: true })}>
                    Tinjau
                  </Button>
                  {customer?.PersonalInfos?.need_review_ktp && (
                    <div className="reupload-icon">
                      <ReuploadIcon />
                      <p>Upload ulang KTP</p>
                    </div>
                  )}
                </>
              )
            }
          />

          <ImageViewer
            label="Passport"
            value={getPassportValue(data, customer, isUploadNewDocumentEnabled)}
            onClick={imagePreviewHandler}
          />
        </div>
      </div>

      {data?.order_detail?.is_for_other_person && (
        <div className="detail-penerima">
          <h2>Detail Penerima</h2>
          <div className="detail-penerima__data">
            <InputField label="Name" disable readOnly value={data?.order_detail?.other_person_name || '-'} />
            <InputField label="Email" disable readOnly value={data?.order_detail?.other_person_email || '-'} />
            <div className="detail-penerima__data__phone-number">
              <InputField
                label="No Handphone"
                readOnly
                disable
                value={data?.order_detail?.other_person_phone_cc || '-'}
                icon={
                  <img
                    src={`https://flagcdn.com/w2560/${selectedCountry?.code?.toLowerCase()}.png`}
                    width={26}
                    height={16}
                    alt={selectedCountry?.name + ' nation flag'}
                  />
                }
                iconPosition={'end'}
              />
              <InputField value={data?.order_detail?.other_person_phone || '-'} disable readOnly />
            </div>
          </div>
        </div>
      )}

      <div className="detail-hari">
        <h2>Detail Hari</h2>
        <div className="detail-hari__data">
          <InputField
            label="Tanggal Mulai"
            icon={<Calendar />}
            iconPosition={'start'}
            disable
            readOnly
            value={data?.order_detail?.start_booking_date || '-'}
          />
          <InputField
            label="Durasi Charter"
            icon={<Duration />}
            iconPosition={'start'}
            disable
            readOnly
            value={data?.order_detail?.booking_zones ? `${data.order_detail.booking_zones?.length} Hari` : '-'}
          />
          <div className="detail-sewa__data__rent-charter-time">
            <RentTimeDetailComponents
              durationRent={data?.order_detail?.booking_zones ? data.order_detail.booking_zones?.length : 0}
              startDate={
                data?.order_detail?.start_booking_date ? new Date(data?.order_detail?.start_booking_date) : new Date()
              }
              allRentTime={mappedAreaCharter(data, data?.order_detail?.vehicle?.charter_pricing)}
            />
          </div>
        </div>
      </div>

      <div className="detail-sewa">
        <h2>Detail Sewa</h2>
        <div className="detail-sewa__data">
          <div className="detail-sewa__data__selected-car">
            <VehicleDetailComponent data={data} />
          </div>

          <InputField label="Tipe Pembayaran" disable readOnly value={getPaymentValue(data)} />

          <InputField
            label="Jumlah Penumpang"
            disable
            readOnly
            value={`${data?.order_detail?.adult_passenger} Adult, ${data?.order_detail?.child_passenger} Child, ${data?.order_detail?.baggage} Suitcase`}
          />
        </div>
      </div>

      <div className="detail-area-charter">
        <h2>Detail Area Charter</h2>
        <AreaCharter
          durationRentDate={data?.order_detail?.booking_zones ? data?.order_detail?.booking_zones?.length : 0}
          startDate={
            data?.order_detail?.start_booking_date ? new Date(data?.order_detail?.start_booking_date) : new Date()
          }
          allAreaCharter={data?.order_detail?.booking_zones ?? []}
          disable
        />
      </div>

      <div className="detail-rincian-biaya">
        <h2>Rincian Biaya</h2>
        <div className="detail-rincian-biaya__data">
          <InputField
            label="Biaya Order"
            disable
            readOnly
            value={data?.booking_price ? setCurrency(data.booking_price, data?.base_currency) : '-'}
          />
          <InputField
            label="Fee Driver"
            disable
            readOnly
            value={data?.fee_driver ? setCurrency(data.fee_driver, data?.base_currency) : '-'}
          />
          <InputField
            label="Outside Operasional"
            disable
            readOnly
            value={
              data?.outside_operational_charge ? setCurrency(data.outside_operational_charge, data?.base_currency) : '-'
            }
          />
          {data?.price_component && data.price_component?.length !== 0 ? (
            data.price_component
              .filter((item) => item?.type === 'charges')
              ?.map((item, idx) => (
                <InputField
                  key={idx}
                  label={`Public Holiday (${item?.label})`}
                  disable
                  readOnly
                  value={item?.amount ? setCurrency(item?.amount, data?.base_currency) : '-'}
                />
              ))
          ) : (
            <InputField label={`Public Holiday`} disable readOnly value={'-'} />
          )}
          <InputField
            label="Biaya Overtime"
            disable
            readOnly
            value={data?.over_time_price ? setCurrency(data.over_time_price, data?.base_currency) : '-'}
          />
          <InputField
            label="Pembayaran Point"
            disable
            readOnly
            icon={<PointsIcon />}
            iconPosition="start"
            value={data?.point ? setCurrency(data.point, 'IDR').replace(/[^\d.,]/g, '') : '-'}
          />
          <InputField
            label="Total Biaya"
            disable
            readOnly
            value={data?.total_payment ? setCurrency(data.total_payment, data?.base_currency) : '-'}
          />
        </div>
      </div>

      <div className="detail-additional">
        <h2>Permintaan Khusus</h2>
        <AddOnsField data={data?.addons} totalPrice={data?.addon_total_price} label={false} />
      </div>

      {setShowBankTransferConfirmation && (
        <Pembayaran
          data={data}
          getFileName={getFileName}
          customer={customer}
          imagePreviewHandler={imagePreviewHandler}
          isPaymentValid={isPaymentValid}
          setShowBankTransferConfirmation={setShowBankTransferConfirmation}
        />
      )}

      <div className="detail-action-btn">
        <Button variant="outline" className="button" width={208} size="sm" onClick={backHandler}>
          Kembali
        </Button>

        {showRejectOrderBtn && (
          <Button
            size="sm"
            className="button"
            width={208}
            variant="danger"
            onClick={() => setShowRejectOrderModal(true)}
          >
            Tolak Pesanan
          </Button>
        )}

        {showCancelOrderBtn && (
          <Button size="sm" className="button" width={208} variant="danger" onClick={handleRequestCancelOrder}>
            Cancel Order
          </Button>
        )}

        {!isPublishTaskCharterFlowEnabled && (
          <RenderedProcessButton
            data={data}
            paymentMethod={paymentMethod}
            customer={customer}
            isPaymentValid={isPaymentValid}
            handleProcess={handleProcess}
          />
        )}
      </div>
    </div>
  );
};

RegularDataForm.propTypes = {
  data: PropTypes.shape({
    transaction_key: PropTypes.string,
    user_name: PropTypes.string,
    phone_country_code: PropTypes.string,
    phone_number: PropTypes.string,
    email: PropTypes.string,
    base_currency: PropTypes.string,

    is_admin_creation: PropTypes.bool,
    order_status: PropTypes.string,

    booking_price: PropTypes.number,
    fee_driver: PropTypes.number,
    outside_operational_charge: PropTypes.number,
    over_time_price: PropTypes.number,
    point: PropTypes.number,
    total_payment: PropTypes.number,

    price_component: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        label: PropTypes.string,
        amount: PropTypes.number,
      }),
    ),

    addons: PropTypes.array,
    addon_total_price: PropTypes.number,

    order_detail: PropTypes.shape({
      start_booking_date: PropTypes.string,
      location_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

      adult_passenger: PropTypes.number,
      child_passenger: PropTypes.number,
      baggage: PropTypes.number,

      is_for_other_person: PropTypes.bool,
      other_person_name: PropTypes.string,
      other_person_email: PropTypes.string,
      other_person_phone: PropTypes.string,
      other_person_phone_cc: PropTypes.string,

      identity: PropTypes.shape({
        ktp: PropTypes.string,
        passport: PropTypes.string,
      }),

      booking_zones: PropTypes.arrayOf(
        PropTypes.shape({
          booking_start_time: PropTypes.string,
          booking_end_time: PropTypes.string,
          charter_duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ),

      vehicle: PropTypes.shape({
        charter_pricing: PropTypes.array,
      }),
    }),
  }),

  customer: PropTypes.shape({
    phone_code: PropTypes.string,
    PersonalInfos: PropTypes.shape({
      ktp: PropTypes.string,
      passport: PropTypes.string,
      need_review_ktp: PropTypes.bool,
      need_review_passport: PropTypes.bool,
    }),
  }),

  selectedCountry: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
  }),

  driverData: PropTypes.shape({
    name: PropTypes.string,
    phone: PropTypes.string,
    license_number: PropTypes.string,
  }),

  imagePreviewHandler: PropTypes.func,
  getFileName: PropTypes.func,
  setShowBankTransferConfirmation: PropTypes.func,
  isPaymentValid: PropTypes.bool,
  approvalUpdate: PropTypes.bool,
  showCancelOrderBtn: PropTypes.bool,
  showRejectOrderBtn: PropTypes.bool,
  transactionKey: PropTypes.string.isRequired,
};

export default RegularDataForm;
